import { useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { usePatchLoanTrackRecordMutation } from '@/apis/extended/loanApplicationApi';
import type {
  LoanApplication,
  LoanTrackRecordRow,
} from '@/types/LoanApplicationType';
import { TRACK_RECORD_PROPERTY_TYPES } from './constants';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

const emptyRow = (): LoanTrackRecordRow => ({
  propertyAddress: '',
  propertyType: '',
  purchasePrice: 0,
  purchaseDate: '',
  renovationBudget: 0,
  salesPrice: 0,
  salesDate: '',
  additionalNotes: '',
});

export function TrackRecordSection({ application, readOnly }: Props) {
  const [patch, { isLoading }] = usePatchLoanTrackRecordMutation();

  const initialRows = useMemo(
    () =>
      (application.trackRecord?.length
        ? application.trackRecord
        : [emptyRow()]
      ).map((r) => ({ ...r })),
    [application._id, application.trackRecord, application.updatedAt]
  );

  const {
    draft: rows,
    setDraft: setRows,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'track-record',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initialRows,
    readOnly,
  });

  const saveSection = async () => {
    if (formReadOnly) return;
    try {
      await patch({
        id: application._id,
        trackRecord: rows,
      }).unwrap();
      markSaved(rows);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly || isLoading;

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Prior investment property experience.
      </Typography>
      {rows.map((row, idx) => (
        <Box
          key={row._id ?? idx}
          sx={{
            mb: 3,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Property address"
                value={row.propertyAddress}
                disabled={disabled}
                onChange={(e) => {
                  const next = [...rows];
                  next[idx] = { ...row, propertyAddress: e.target.value };
                  setRows(next);
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Property type"
                value={row.propertyType}
                disabled={disabled}
                onChange={(e) => {
                  const next = [...rows];
                  next[idx] = { ...row, propertyType: e.target.value };
                  setRows(next);
                }}
              >
                <MenuItem value="">Select…</MenuItem>
                {TRACK_RECORD_PROPERTY_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Purchase price"
                type="number"
                value={row.purchasePrice}
                disabled={disabled}
                onChange={(e) => {
                  const next = [...rows];
                  next[idx] = {
                    ...row,
                    purchasePrice: Number(e.target.value) || 0,
                  };
                  setRows(next);
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Purchase date"
                type="date"
                value={row.purchaseDate}
                disabled={disabled}
                onChange={(e) => {
                  const next = [...rows];
                  next[idx] = { ...row, purchaseDate: e.target.value };
                  setRows(next);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Renovation budget"
                type="number"
                value={row.renovationBudget}
                disabled={disabled}
                onChange={(e) => {
                  const next = [...rows];
                  next[idx] = {
                    ...row,
                    renovationBudget: Number(e.target.value) || 0,
                  };
                  setRows(next);
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Sales price"
                type="number"
                value={row.salesPrice}
                disabled={disabled}
                onChange={(e) => {
                  const next = [...rows];
                  next[idx] = {
                    ...row,
                    salesPrice: Number(e.target.value) || 0,
                  };
                  setRows(next);
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Sales date"
                type="date"
                value={row.salesDate}
                disabled={disabled}
                onChange={(e) => {
                  const next = [...rows];
                  next[idx] = { ...row, salesDate: e.target.value };
                  setRows(next);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={row.additionalNotes}
                disabled={disabled}
                onChange={(e) => {
                  const next = [...rows];
                  next[idx] = { ...row, additionalNotes: e.target.value };
                  setRows(next);
                }}
                multiline
                minRows={2}
              />
            </Grid>
            {!formReadOnly && (
              <Grid item xs={12}>
                <IconButton
                  onClick={() => setRows((R) => R.filter((_, i) => i !== idx))}
                  disabled={rows.length <= 1}
                >
                  <i className="fa-light fa-trash" />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Box>
      ))}
      {!formReadOnly && (
        <Box sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setRows((R) => [...R, emptyRow()])}
          >
            Add property
          </Button>
        </Box>
      )}
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save track record"
        onSave={() => void saveSection()}
        onCancel={resetDraft}
      />
    </div>
  );
}
